import axiosNest from '../../api/axiosNest';

/**
 * Powiadomienia działów FTL i Trade — wywołania API
 * (`apps/api/src/notifications/notifications.controller.ts`).
 */

export const NOTIFICATIONS_URL = '/notifications';

export const DEPARTMENT_LABELS: Record<string, string> = {
  FTL: 'FTL',
  TRADE: 'Trade',
};

export interface NotificationItem {
  id: number;
  kind: string;
  audience: string[];
  text: string;
  at: string;
  unread: boolean;
  /** `null` = system (import z TMS, kolejka) — na ekranie „system”. */
  by: string | null;
  order: { id: number; externalId: string | null };
  /**
   * Ważny alert (reguła API `user-offers/alert-importance.ts`, 12.10.2026) —
   * na górze listy i na czerwono. Brak = zwykłe powiadomienie.
   */
  important?: boolean;
  /**
   * Powiadomienie imienne (20.10.2026, przełącznik `variantNotifications`):
   * dla tej osoby, nie dla działu. Brak = działowe.
   */
  personal?: boolean;
  /**
   * Alert, którego warunek już nie trwa — „nieaktualne” (24.10.2026,
   * przełącznik `notificationReadState`). Brak = przełącznik wyłączony.
   */
  stale?: boolean;
  /** Klucz do „Oznacz jako przeczytane” — tylko z `notificationReadState`. */
  key?: string;
}

/**
 * „Przeczytane” osobno i „nieaktualne” (24.10.2026, S-24.10-09) — klucz jak w API.
 */
export const FEATURE_NOTIFICATION_READ_STATE = 'notificationReadState';

/** Klucz przełącznika powiadomień imiennych — jak w API. */
export const FEATURE_VARIANT_NOTIFICATIONS = 'variantNotifications';
/**
 * Imienne z makiety FTL (24.10.2026, S-24.10-10): opiekun pojazdu, Team
 * Leader, autor przekazania do Trade, autor wariantu — klucz jak w API.
 */
export const FEATURE_NAMED_NOTIFICATIONS = 'namedNotifications';

/** Komu: imienne — „Ciebie”, działowe — nazwy działów. */
export function audienceText(item: Pick<NotificationItem, 'audience' | 'personal'>): string {
  if (item.personal) return 'Ciebie';
  return item.audience.map((d) => DEPARTMENT_LABELS[d] ?? d).join(', ');
}

export interface NotificationFeed {
  items: NotificationItem[];
  unread: number;
  /** Ile nieprzeczytanych to ważne alerty — czerwony licznik. Brak = 0. */
  importantUnread?: number;
  readAt: string | null;
  /** Śledzone działy; pusta lista = wszystkie. */
  followed: string[];
  departments: string[];
}

export async function fetchNotifications(): Promise<NotificationFeed> {
  const { data } = await axiosNest.get<NotificationFeed>(NOTIFICATIONS_URL);
  return data;
}

/**
 * Strona „Powiadomienia” z historią (15.10.2026, Etap 1 makiety FTL,
 * przełącznik `notificationsPage`) — `GET /notifications/history`.
 * Wartości filtrów to dane API (adres), nie tłumaczymy.
 */
export type HistoryFilters = {
  days: number;
  /** `followed` (jak dzwonek), `all` albo kod działu. */
  department: string;
  /** Pusty = wszystkie rodzaje. */
  kind: string;
  read: 'all' | 'unread' | 'read';
  /** „Tylko moje” — same powiadomienia imienne (20.10.2026). */
  mine?: boolean;
  /**
   * „Aktualność” (24.10.2026, `notificationReadState`): wartości API —
   * `all`, `current`, `stale`. Brak = wszystkie.
   */
  current?: 'all' | 'current' | 'stale';
};

export const DEFAULT_HISTORY_FILTERS: HistoryFilters = {
  days: 30,
  department: 'followed',
  kind: '',
  read: 'all',
};

/** Okresy strony (dni) — jak `HISTORY_PERIODS` w API. */
const HISTORY_DAYS = [7, 14, 30, 90];
const READ_FILTERS: ReadonlyArray<HistoryFilters['read']> = ['all', 'unread', 'read'];

/**
 * Filtry z adresu strony (`/notifications?mine=1&read=unread`) — tam
 * prowadzi kafelek „Moje powiadomienia” pulpitu planisty (makieta back office
 * v2, A4, 20.10.2026), z tymi samymi parametrami, którymi API go policzyło.
 * Czego adres nie podaje albo podaje źle — wartość domyślna.
 */
export function historyFiltersFromAddress(params: URLSearchParams): HistoryFilters {
  const filters: HistoryFilters = { ...DEFAULT_HISTORY_FILTERS };
  const days = Number(params.get('days'));
  if (HISTORY_DAYS.includes(days)) filters.days = days;
  const department = params.get('department');
  if (department) filters.department = department;
  const kind = params.get('kind');
  if (kind) filters.kind = kind;
  const read = params.get('read');
  if (read && (READ_FILTERS as readonly string[]).includes(read)) {
    filters.read = read as HistoryFilters['read'];
  }
  const mine = params.get('mine');
  if (mine === '1' || mine === 'true') filters.mine = true;
  const current = params.get('current');
  if (current === 'current' || current === 'stale') filters.current = current;
  return filters;
}

export interface NotificationHistory {
  items: NotificationItem[];
  total: number;
  unread: number;
  /** Okres nie zmieścił się w limicie — lista pełna od `coveredFrom`. */
  truncated: boolean;
  coveredFrom: string | null;
  readAt: string | null;
  followed: string[];
  departments: string[];
  /** Rodzaje, które to konto może dostać — do filtra. */
  kinds: { kind: string; label: string }[];
  periods: number[];
}

export async function fetchNotificationHistory(
  filters: HistoryFilters,
): Promise<NotificationHistory> {
  const { data } = await axiosNest.get<NotificationHistory>(`${NOTIFICATIONS_URL}/history`, {
    params: {
      days: filters.days,
      department: filters.department,
      read: filters.read,
      ...(filters.kind ? { kind: filters.kind } : {}),
      ...(filters.mine ? { mine: 1 } : {}),
      ...(filters.current && filters.current !== 'all' ? { current: filters.current } : {}),
    },
  });
  return data;
}

/** „Oznacz jako przeczytane” jednej pozycji (24.10.2026, S-24.10-09). */
export async function markNotificationRead(key: string): Promise<void> {
  await axiosNest.post(`${NOTIFICATIONS_URL}/read-item`, { key });
}

export async function markNotificationsRead(): Promise<void> {
  await axiosNest.post(`${NOTIFICATIONS_URL}/read`);
}

export async function setFollowedDepartments(departments: string[]): Promise<void> {
  await axiosNest.put(`${NOTIFICATIONS_URL}/departments`, { departments });
}

/**
 * Zaznaczenie działów → wartość dla API. Wszystkie zaznaczone to pusta lista
 * („wszystkie”), żeby dział dodany kiedyś do słownika nie wypadł po cichu
 * komuś, kto zaznaczył „wszystko, co było”.
 */
export function followedForApi(selected: string[], all: string[]): string[] {
  return all.every((d) => selected.includes(d)) ? [] : selected;
}

/**
 * Ważne na górze, w obu grupach kolejność z API (najnowsze pierwsze). API
 * już tak układa listę — tu tylko na wypadek odpowiedzi sprzed zmiany.
 */
export function importantFirst(items: readonly NotificationItem[]): NotificationItem[] {
  return [...items.filter((i) => i.important), ...items.filter((i) => !i.important)];
}

/** Wartość z API → zaznaczenie na ekranie. Pusta lista = wszystkie zaznaczone. */
export function selectedFromApi(followed: string[], all: string[]): string[] {
  return followed.length === 0 ? [...all] : followed;
}
